import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Loader2, Link2, Eye, EyeOff, ExternalLink, FileDown, FileSpreadsheet, Pencil, Copy, QrCode, Share2, ShoppingCart } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import CatalogEditDialog from '@/pages/CatalogEditDialog';
import CatalogQRDialog from '@/pages/CatalogQRDialog';
import CatalogShareDialog from '@/pages/CatalogShareDialog';
import CatalogConvertDialog from '@/pages/CatalogConvertDialog';

// Phase 1 catalog admin: list + create + rename + delete.
// Item picker + share link + PDF land in Phase 2/3.
export default function CatalogsTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qr, setQr] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [converting, setConverting] = useState(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-linked ?edit=<id>&edit_tab=items opens the edit dialog automatically
  // — used by the Catalog bulk-add "Reorder after add" flow.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !items.length) return;
    const match = items.find((c) => c.id === editId);
    if (match) {
      setEditing({ ...match, __openTab: searchParams.get('edit_tab') || 'details' });
      const next = new URLSearchParams(searchParams);
      next.delete('edit'); next.delete('edit_tab');
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/catalogs');
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  const copyLink = async (c) => {
    if (c.visibility !== 'public') {
      toast.info('Make this catalog public first (edit visibility to share)');
      return;
    }
    const url = `${window.location.origin}/c/${c.slug}`;
    try { await navigator.clipboard.writeText(url); toast.success('Public link copied'); }
    catch { toast.error('Copy failed — link: ' + url); }
  };
  const preview = (c) => {
    window.open(`/c/${c.slug}`, '_blank', 'noopener,noreferrer');
  };
  const downloadPdf = async (c) => {
    try {
      const resp = await api.get(`/public/catalogs/${c.slug}/pricelist.pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Catalog not found' : 'Could not download PDF');
    }
  };
  const downloadXlsx = async (c) => {
    try {
      const resp = await api.get(`/public/catalogs/${c.slug}/pricelist.xlsx`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `ijehaus-${c.slug}-pricelist.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Catalog not found' : 'Could not download Excel');
    }
  };
  const togglePublic = async (c) => {
    const next = c.visibility === 'public' ? 'private' : 'public';
    try {
      await api.patch(`/catalogs/${c.id}`, { visibility: next });
      toast.success(next === 'public' ? 'Now public' : 'Now private');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  useEffect(() => { load(); }, []);

  const visibleItems = mineOnly && user
    ? items.filter((c) => c.owner_id === user.id)
    : items;

  const create = async () => {
    if (!newName.trim()) {
      toast.info('Type a name in the field first, then tap Create');
      return;
    }
    setBusy(true);
    try {
      await api.post('/catalogs', { name: newName.trim(), item_skus: [] });
      setNewName('');
      toast.success('Catalog created');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/catalogs/${c.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const duplicate = async (c) => {
    try {
      const { data } = await api.post(`/catalogs/${c.id}/duplicate`);
      toast.success(`Duplicated as "${data.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    }
  };

  return (
    <div data-testid="admin-catalogs-panel">
      {/* Create row — stacks on mobile */}
      <div className="mb-5 md:mb-6 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex gap-2 md:contents">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="New catalog name…"
            className="ije-input h-11 md:h-10 flex-1 md:flex-none md:w-72"
            data-testid="new-catalog-name"
          />
          <button
            type="button"
            onClick={create}
            disabled={busy}
            className="btn-primary h-11 md:h-10 whitespace-nowrap"
            data-testid="new-catalog-create"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </button>
        </div>
        <div className="flex items-center justify-between md:justify-end md:ml-auto gap-3">
          <button
            type="button"
            onClick={() => setMineOnly((v) => !v)}
            className={`sans text-[10px] tracking-widest uppercase px-2.5 py-1.5 border transition-colors ${mineOnly ? 'border-black bg-neutral-900 text-white' : 'border-neutral-300 text-neutral-500 hover:border-neutral-500'}`}
            title="Toggle mine-only filter"
            data-testid="catalog-mine-only-toggle"
          >
            {mineOnly ? 'Mine only' : 'All catalogs'}
          </button>
          <span className="sans text-xs text-neutral-500">{visibleItems.length} catalogs</span>
        </div>
      </div>

      {loading ? (
        <p className="sans text-sm text-neutral-500 py-6">Loading…</p>
      ) : visibleItems.length === 0 ? (
        <div className="border border-dashed border-neutral-200 p-12 text-center sans text-sm text-neutral-500">
          {mineOnly ? 'You don\'t own any catalogs yet.' : 'No custom catalogs yet. Create one above.'}
        </div>
      ) : (
        <>
        {/* Mobile card list (< md) */}
        <div className="md:hidden space-y-3">
          {visibleItems.map((c) => (
            <div key={c.id} className="border border-neutral-200 p-4" data-testid={`catalog-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="serif text-base leading-tight truncate">{c.name}</p>
                  <p className="sans text-[11px] text-neutral-500 font-mono truncate mt-0.5">{c.slug}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="sans text-[10px] tracking-widest uppercase text-neutral-500">
                      {(c.item_skus || []).length} items
                    </span>
                    <span className="text-neutral-300">·</span>
                    <span className={`sans text-[10px] tracking-widest uppercase ${c.visibility === 'public' ? 'text-[color:var(--ije-pink-dark)]' : 'text-neutral-500'}`}>
                      {c.visibility === 'public' ? 'Public' : 'Private'}
                    </span>
                  </div>
                  {c.owner_email && (
                    <p className="sans text-[11px] text-neutral-500 truncate mt-1.5">{c.owner_email}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => togglePublic(c)}
                  className={`p-2 -m-2 shrink-0 ${c.visibility === 'public' ? 'text-[color:var(--ije-pink-dark)]' : 'text-neutral-500'}`}
                  title={c.visibility === 'public' ? 'Public — tap to make private' : 'Private — tap to make public'}
                  data-testid={`catalog-visibility-${c.id}`}
                >
                  {c.visibility === 'public' ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>

              {/* Action grid — 4 primary buttons, then overflow */}
              <div className="grid grid-cols-4 gap-1 mt-4 pt-3 border-t border-neutral-100">
                <button
                  type="button"
                  onClick={() => setEditing(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-edit-${c.id}`}
                >
                  <Pencil size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">Edit</span>
                </button>
                <button
                  type="button"
                  onClick={() => preview(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-preview-${c.id}`}
                >
                  <ExternalLink size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">View</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSharing(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-share-${c.id}`}
                >
                  <Share2 size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">Share</span>
                </button>
                <button
                  type="button"
                  onClick={() => setQr(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-qr-${c.id}`}
                >
                  <QrCode size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">QR</span>
                </button>
              </div>
              <div className="grid grid-cols-4 gap-1 mt-1">
                <button
                  type="button"
                  onClick={() => setConverting(c)}
                  disabled={(c.item_skus || []).length === 0}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`catalog-convert-${c.id}`}
                  title={(c.item_skus || []).length === 0 ? 'Add items first' : 'Convert to order'}
                >
                  <ShoppingCart size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">Order</span>
                </button>
                <button
                  type="button"
                  onClick={() => copyLink(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-copy-${c.id}`}
                >
                  <Link2 size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">Link</span>
                </button>
                <button
                  type="button"
                  onClick={() => downloadPdf(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-pdf-${c.id}`}
                >
                  <FileDown size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">PDF</span>
                </button>
                <button
                  type="button"
                  onClick={() => downloadXlsx(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-xlsx-${c.id}`}
                >
                  <FileSpreadsheet size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">Excel</span>
                </button>
                <button
                  type="button"
                  onClick={() => duplicate(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm"
                  data-testid={`catalog-duplicate-${c.id}`}
                >
                  <Copy size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">Copy</span>
                </button>
              </div>
              <div className="grid grid-cols-4 gap-1 mt-1">
                <button
                  type="button"
                  onClick={() => remove(c)}
                  className="flex flex-col items-center gap-1 py-2 text-neutral-500 active:bg-red-50 active:text-red-600 rounded-sm"
                  data-testid={`catalog-delete-${c.id}`}
                >
                  <Trash2 size={16} />
                  <span className="sans text-[9px] tracking-widest uppercase">Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table (md+) */}
        <div className="hidden md:block border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-right px-4 py-3">Items</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {visibleItems.map((c) => (
                <tr key={c.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 sans text-neutral-900">{c.name}</td>
                  <td className="px-4 py-3 sans text-xs text-neutral-500 font-mono">{c.slug}</td>
                  <td className="px-4 py-3 sans text-neutral-700 text-right">{(c.item_skus || []).length}</td>
                  <td className="px-4 py-3 sans text-xs text-neutral-500">{c.owner_email}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => togglePublic(c)}
                        className={`p-1 ${c.visibility === 'public' ? 'text-[color:var(--ije-pink-dark)]' : 'text-neutral-500 hover:text-black'}`}
                        title={c.visibility === 'public' ? 'Public — click to make private' : 'Private — click to make public'}
                        data-testid={`catalog-visibility-${c.id}`}
                      >
                        {c.visibility === 'public' ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyLink(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Copy public link"
                        data-testid={`catalog-copy-${c.id}`}
                      >
                        <Link2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => preview(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Preview catalog in new tab"
                        data-testid={`catalog-preview-${c.id}`}
                      >
                        <ExternalLink size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadPdf(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Download price list PDF"
                        data-testid={`catalog-pdf-${c.id}`}
                      >
                        <FileDown size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadXlsx(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Download price list Excel"
                        data-testid={`catalog-xlsx-${c.id}`}
                      >
                        <FileSpreadsheet size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Edit catalog"
                        data-testid={`catalog-edit-${c.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setSharing(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Share with members or invite links"
                        data-testid={`catalog-share-${c.id}`}
                      >
                        <Share2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setQr(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Show QR code"
                        data-testid={`catalog-qr-${c.id}`}
                      >
                        <QrCode size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => duplicate(c)}
                        className="p-1 text-neutral-500 hover:text-black"
                        title="Duplicate catalog"
                        data-testid={`catalog-duplicate-${c.id}`}
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConverting(c)}
                        disabled={(c.item_skus || []).length === 0}
                        className="p-1 text-neutral-500 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                        title={(c.item_skus || []).length === 0 ? 'Add items first' : 'Convert to order'}
                        data-testid={`catalog-convert-${c.id}`}
                      >
                        <ShoppingCart size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(c)}
                        className="text-neutral-500 hover:text-red-600 p-1"
                        title="Delete catalog"
                        data-testid={`catalog-delete-${c.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <CatalogEditDialog
        catalog={editing}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onSaved={load}
        onConvertRequest={(c) => setConverting(c)}
      />

      <CatalogQRDialog
        catalog={qr}
        open={!!qr}
        onOpenChange={(v) => !v && setQr(null)}
      />

      <CatalogShareDialog
        catalog={sharing}
        open={!!sharing}
        onOpenChange={(v) => !v && setSharing(null)}
        onChanged={load}
      />

      <CatalogConvertDialog
        catalog={converting}
        open={!!converting}
        onOpenChange={(v) => !v && setConverting(null)}
      />
    </div>
  );
}
